'use client'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ImageIcon, User, UserX, X } from 'lucide-react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'
import ReactQuill from 'react-quill'

import 'katex/dist/katex.min.css'
import 'highlight.js/styles/atom-one-dark.css'

import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'

import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { EmojiPicker } from '@/components/ui/emoji-picker'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'

import { useAuth } from '@/contexts/auth-context'
import memeService from '@/services/meme.service'
import uploadService from '@/services/upload.service'
import userService from '@/services/user.service'
import { normalizeForumMentionHtmlForEditor } from '@/utils/forum-mention'

import MathDialog from './MathDialog'
import QuillWrapper from './QuillWrapper'

import { MemePost } from '@/types/meme.interface'
import { UserProfile } from '@/types/user.type'

const CodeBlockDialog = dynamic(() => import('./CodeBlockDialog').then(mod => mod), { ssr: false })

const extractHashtags = (text: string): string[] => {
  const hashtagRegex = /#(\w+)/g
  const hashtags: string[] = []
  let match

  while ((match = hashtagRegex.exec(text)) !== null) {
    const hashtag = match[1] // Get the word without the #
    if (!hashtags.includes(hashtag)) {
      hashtags.push(hashtag)
    }
  }

  return hashtags
}

interface MentionableUser {
  id: number
  displayName: string
  avatar?: string
  profileId: string
}

interface MemePostEditorProps {
  setIsOpen: (isOpen: boolean) => void
  editPost?: MemePost | null
}

/** Matches an in-progress @mention query at the end of the editor text (supports Unicode names). */
const MENTION_TRIGGER_REGEX = /@([^\s@]*)$/

function normalizeEditorContent(content?: string | null): string {
  return content ? normalizeForumMentionHtmlForEditor(content) : ''
}

export default function MemePostEditor({ setIsOpen, editPost }: MemePostEditorProps) {
  const { user } = useAuth()
  const [content, setContent] = React.useState(() => normalizeEditorContent(editPost?.content))
  const [uploadedImages, setUploadedImages] = React.useState<string[]>(editPost?.images?.map(img => img.imageUrl) || [])
  const [fileImages, setFileImages] = React.useState<File[]>([])
  const [isUploading, setIsUploading] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [isAnonymous, setIsAnonymous] = React.useState(editPost?.isAnonymous || false)

  const [isMathDialogOpen, setIsMathDialogOpen] = React.useState(false)
  const [editingMath, setEditingMath] = React.useState<{ node: HTMLElement; latex: string } | null>(null)

  const [isCodeBlockDialogOpen, setIsCodeBlockDialogOpen] = React.useState(false)
  const [editingCodeBlock, setEditingCodeBlock] = React.useState<{
    node: HTMLElement
    code: string
    language: string
  } | null>(null)

  // Mention functionality state
  const [showMentionDropdown, setShowMentionDropdown] = React.useState(false)
  const [mentionQuery, setMentionQuery] = React.useState('')
  const [mentionPosition, setMentionPosition] = React.useState({ x: 0, y: 0 })
  const [selectedUserIndex, setSelectedUserIndex] = React.useState(0)
  const [_mentionContext, setMentionContext] = React.useState<{
    node: Node | null
    offset: number
  }>({ node: null, offset: 0 })
  const [filteredUsers, setFilteredUsers] = React.useState<MentionableUser[]>([])
  const [taggedUsers, setTaggedUsers] = React.useState<number[]>([])

  const channelId = useSearchParams().get('channelId')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const quillRef = React.useRef<ReactQuill | null>(null as any)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()
  const isEditMode = !!editPost
  const debouncedMentionQuery = useDebounce(mentionQuery, 300)

  const { data: searchResults = [], isFetching: isSearchingUsers } = useQuery({
    queryKey: ['user-search', debouncedMentionQuery],
    queryFn: () => userService.searchUsers({ query: debouncedMentionQuery }),
    enabled: showMentionDropdown && debouncedMentionQuery.length > 0,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  })

  React.useEffect(() => {
    if (editPost?.images) {
      setUploadedImages(editPost.images.map(img => img.imageUrl))
    }
  }, [editPost])

  React.useEffect(() => {
    if (!editPost?.content) {
      return
    }
    setContent(normalizeEditorContent(editPost.content))
  }, [editPost?.content, editPost?.id])

  React.useEffect(() => {
    if (searchResults) {
      const mentionableUsers: MentionableUser[] = searchResults.map((user: UserProfile) => ({
        id: user.id,
        displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        avatar: user.avatar,
        profileId: user.profileId,
      }))
      setFilteredUsers(mentionableUsers)
    }
  }, [searchResults])

  React.useEffect(() => {
    setSelectedUserIndex(0)
  }, [mentionQuery])

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (showMentionDropdown || isMathDialogOpen || isCodeBlockDialogOpen) return
      setIsOpen(false)
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showMentionDropdown, isMathDialogOpen, isCodeBlockDialogOpen, setIsOpen])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current?.contains(event.target as Node)) {
        return
      }

      // Keep dropdown open while interacting with the Quill editor.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (editor?.root?.contains(event.target as Node)) {
        return
      }

      setShowMentionDropdown(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  React.useEffect(() => {
    let editor: {
      on: (event: string, handler: (...args: unknown[]) => void) => void
      off: (event: string, handler: (...args: unknown[]) => void) => void
      getSelection: (focus?: boolean) => { index: number } | null
      getText: (start: number, end?: number) => string
      getBounds: (index: number) => { left: number; bottom: number }
      root: HTMLElement
    } | null = null
    let cancelled = false
    let pollTimer: ReturnType<typeof setInterval> | null = null

    const syncMentionState = () => {
      if (!editor) return

      const selection = editor.getSelection(true)
      if (!selection) return

      const text = editor.getText(0, selection.index)
      const mentionMatch = text.match(MENTION_TRIGGER_REGEX)

      if (mentionMatch) {
        setMentionQuery(mentionMatch[1])
        setShowMentionDropdown(true)

        const bounds = editor.getBounds(selection.index)
        const editorRect = editor.root.getBoundingClientRect()
        setMentionPosition({
          x: editorRect.left + bounds.left,
          y: editorRect.top + bounds.bottom + 4,
        })
      } else {
        setShowMentionDropdown(false)
        setMentionQuery('')
      }
    }

    const onTextChange = (...args: unknown[]) => {
      if (args[2] !== 'user') return
      requestAnimationFrame(syncMentionState)
    }

    const attachListener = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      editor = (quillRef.current as any)?.getEditor?.() ?? null
      if (!editor || cancelled) return false

      editor.on('text-change', onTextChange)
      return true
    }

    if (!attachListener()) {
      pollTimer = setInterval(() => {
        if (attachListener() && pollTimer) {
          clearInterval(pollTimer)
          pollTimer = null
        }
      }, 100)
    }

    return () => {
      cancelled = true
      if (pollTimer) clearInterval(pollTimer)
      if (editor) editor.off('text-change', onTextChange)
    }
  }, [editPost?.id])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showMentionDropdown) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedUserIndex(prev => (prev < filteredUsers.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          event.preventDefault()
          setSelectedUserIndex(prev => (prev > 0 ? prev - 1 : filteredUsers.length - 1))
          break
        case 'Enter':
          event.preventDefault()
          if (filteredUsers[selectedUserIndex]) {
            handleInsertMention(filteredUsers[selectedUserIndex])
          }
          break
        case 'Escape':
          event.preventDefault()
          setShowMentionDropdown(false)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMentionDropdown, filteredUsers, selectedUserIndex])

  React.useEffect(() => {
    if (content && quillRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (editor && editor.getModule('math')) {
        // Small delay to ensure the editor is fully initialized
        setTimeout(() => {
          editor.getModule('math').rerenderMathBlocks()
        }, 200)
      }
    }
  }, [content])

  React.useEffect(() => {
    if (editPost?.content && quillRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (editor && editor.getModule('math')) {
        // Longer delay for initial mount to ensure everything is ready
        setTimeout(() => {
          editor.getModule('math').rerenderMathBlocks()
        }, 500)
      }
    }
  }, [editPost?.content])

  React.useEffect(() => {
    const handleMathDialog = () => {
      setEditingMath(null)
      setIsMathDialogOpen(true)
    }

    const handleMathEdit = (event: CustomEvent) => {
      const { latex, node } = event.detail
      const currentLatex = node.getAttribute('data-latex') || latex
      setEditingMath({ node, latex: currentLatex })
      setIsMathDialogOpen(true)
    }

    const handleCodeBlockDialog = () => {
      setEditingCodeBlock(null)
      setIsCodeBlockDialogOpen(true)
    }

    const handleCodeBlockEdit = (event: CustomEvent) => {
      const { code, language, node } = event.detail
      const currentCode = node.getAttribute('data-code') || code
      const currentLanguage = node.getAttribute('data-language') || language
      setEditingCodeBlock({ node, code: currentCode, language: currentLanguage })
      setIsCodeBlockDialogOpen(true)
    }

    window.addEventListener('quill-math-dialog', handleMathDialog)
    window.addEventListener('quill-math-edit', handleMathEdit as EventListener)
    window.addEventListener('quill-codeblock-dialog', handleCodeBlockDialog)
    window.addEventListener('quill-codeblock-edit', handleCodeBlockEdit as EventListener)

    return () => {
      window.removeEventListener('quill-math-dialog', handleMathDialog)
      window.removeEventListener('quill-math-edit', handleMathEdit as EventListener)
      window.removeEventListener('quill-codeblock-dialog', handleCodeBlockDialog)
      window.removeEventListener('quill-codeblock-edit', handleCodeBlockEdit as EventListener)
    }
  }, [])

  const handleInsertMention = React.useCallback((mentionUser: MentionableUser) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editor = (quillRef.current as any)?.getEditor?.()
    if (!editor) return

    const range = editor.getSelection(true)
    if (!range) return

    const index = range.index
    const mentionText = `@${mentionUser.displayName}`

    const text = editor.getText(0, index)
    const mentionMatch = text.match(MENTION_TRIGGER_REGEX)
    if (mentionMatch) {
      const startIndex = index - mentionMatch[0].length

      editor.deleteText(startIndex, mentionMatch[0].length)
      editor.setSelection(startIndex, 0, 'silent')

      const mentionModule = editor.getModule('mention')
      if (mentionModule && mentionModule.insertMention) {
        mentionModule.insertMention({
          id: mentionUser.profileId.toString(),
          displayName: mentionUser.displayName,
          profileId: mentionUser.profileId,
        })
      } else {
        const mentionData = {
          id: mentionUser.profileId.toString(),
          displayName: mentionUser.displayName,
          text: mentionText,
        }

        editor.insertEmbed(startIndex, 'mention', mentionData)
        editor.insertText(startIndex + 1, ' ')
        editor.setSelection(startIndex + 2)
      }
    }

    editor.focus()
    setContent(editor.root.innerHTML)

    setTaggedUsers(prevSelected => {
      if (prevSelected.some(prevUserId => prevUserId === mentionUser.id)) {
        return prevSelected
      }
      return [...prevSelected, mentionUser.id]
    })

    setShowMentionDropdown(false)
    setMentionContext({ node: null, offset: 0 })
  }, [])

  const handleQuillChange = React.useCallback((newContent: string) => {
    setContent(newContent)
  }, [])

  const imageHandler = () => {
    if (!fileInputRef.current) return

    fileInputRef.current.value = ''
    fileInputRef.current.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    try {
      const newFiles = Array.from(files)
      setFileImages(prev => [...prev, ...newFiles])
      const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file))
      setUploadedImages(prev => [...prev, ...newPreviewUrls])
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading image:', error)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const removeImage = (imageUrl: string) => {
    setUploadedImages(prev => prev.filter(img => img !== imageUrl))
    URL.revokeObjectURL(imageUrl)

    const imageIndex = uploadedImages.indexOf(imageUrl)
    if (imageIndex === -1) return
    setFileImages(prev => prev.filter((_, index) => index !== imageIndex))
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (editor && editor.getModule('math')) {
        await editor.getModule('math').forceRerenderAllMathBlocks()
      }

      const finalContent = editor ? editor.root.innerHTML : content

      const plainTextContent = finalContent.replace(/<[^>]*>/g, '') // Remove HTML tags
      const hashtags = extractHashtags(plainTextContent)

      if (isEditMode && editPost) {
        const newImageUpdates = await Promise.all(
          fileImages.map(file => uploadService.uploadImageComment({ image: file }))
        )
        const previousImageUrls = uploadedImages.filter(url => !url.includes('blob:'))
        const updatedImageUrls = [...previousImageUrls, ...newImageUpdates.map(update => update.url)]
        await memeService.updateMeme(editPost.id, {
          content: finalContent,
          imageUrls: updatedImageUrls,
          hashtags,
          isAnonymous,
          channelId,
          mentionedUserIds: taggedUsers,
        })
      } else {
        const imageUrls =
          fileImages.length > 0
            ? (
                await Promise.all(
                  fileImages.map(file => uploadService.uploadImageComment({ image: file }))
                )
              ).map(upload => upload.url)
            : []

        await memeService.createMeme({
          content: finalContent,
          imageUrls,
          hashtags,
          isAnonymous,
          channelId,
          mentionedUserIds: taggedUsers,
        })
      }

      setIsOpen(false)
      setTaggedUsers([])
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'], refetchType: 'active' })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting post:', error)
    } finally {
      setLoading(false)
      setIsOpen(false)
    }
  }

  const insertEmoji = React.useCallback(
    (emoji: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (!editor) {
        setContent(prev => prev + emoji)
        return
      }
      const range = editor.getSelection(true)
      const index = range ? range.index : editor.getLength()

      if (emoji.startsWith('<img')) {
        // Fallback if a raw <img> was passed
        editor.clipboard.dangerouslyPasteHTML(index, emoji)
        editor.setSelection(index + 1)
      } else {
        editor.insertText(index, emoji)
        editor.setSelection(index + emoji.length)
      }
      editor.focus()
    },
    [setContent]
  )

  const insertCustomEmoji = React.useCallback(
    (emoji: { id: number; name: string; imageUrl: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (!editor) {
        // eslint-disable-next-line no-console
        console.warn('Editor not available. Inserting raw HTML fallback.')
        setContent(
          prev => `${prev}<img src="${emoji.imageUrl}" alt=":${emoji.name}:" style="width:24px;height:24px;" />`
        )
        return
      }

      try {
        const range = editor.getSelection(true)
        const index = range ? range.index : editor.getLength()
        editor.insertEmbed(index, 'customEmoji', {
          src: emoji.imageUrl,
          alt: `:${emoji.name}:`,
          id: String(emoji.id ?? ''),
          name: emoji.name ?? 'emoji',
        })

        editor.setSelection(index + 1)
        editor.focus()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to insert custom emoji:', error)
      }
    },
    [setContent]
  )

  const insertMath = React.useCallback(
    (latex: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (!editor) {
        // Fallback if editor is not available - always add newline before and after
        setContent(prev => {
          const before = prev.length > 0 && !prev.endsWith('\n') ? '\n' : ''
          return prev + before + `$$${latex}$$\n`
        })
        return
      }

      try {
        // Use the math module's insertMath method if available (it handles newlines properly)
        const mathModule = editor.getModule('math')
        if (mathModule && mathModule.insertMath) {
          mathModule.insertMath(latex, true)
        } else {
          // Fallback method with proper newline handling
          const range = editor.getSelection(true)
          let index = range ? range.index : editor.getLength()

          // Always add newline before
          editor.insertText(index, '\n')
          index += 1

          editor.insertEmbed(index, 'math', {
            latex,
            display: true,
          })

          // Ensure newline after
          editor.insertText(index + 1, '\n')

          // Set cursor after the math block
          editor.setSelection(index + 2)
          editor.focus()
        }

        const newContent = editor.root.innerHTML
        setContent(newContent)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to insert math:', error)
      }
    },
    [setContent]
  )

  const updateMath = React.useCallback(
    (latex: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (editor && editor.getModule('math')) {
        editor.getModule('math').updateMath(editingMath?.node, latex, () => {
          const newContent = editor.root.innerHTML
          setContent(newContent)

          // Ensure Quill reflows without modifying content
          const currentSel = editor.getSelection(true)
          if (currentSel) {
            editor.setSelection(currentSel.index, currentSel.length, 'silent')
          } else {
            editor.update('silent')
          }
        })

        setEditingMath(prev => (prev ? { ...prev, latex } : null))
      }
    },
    [editingMath, setContent]
  )

  const insertCodeBlock = React.useCallback(
    (code: string, language: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (!editor) {
        // Fallback if editor is not available
        setContent(prev => {
          const needsNewlineBefore = prev.length > 0 && !prev.endsWith('\n')
          const before = needsNewlineBefore ? '\n' : ''
          return prev + before + `<pre><code class="language-${language}">${code}</code></pre>\n`
        })
        return
      }

      try {
        // Use the codeBlock module's insertCodeBlock method if available (it handles newlines properly)
        const codeBlockModule = editor.getModule('codeBlock')
        if (codeBlockModule && codeBlockModule.insertCodeBlock) {
          codeBlockModule.insertCodeBlock(code, language)
        } else {
          // Fallback method with proper newline handling
          const range = editor.getSelection(true)
          let index = range ? range.index : editor.getLength()

          // Always add newline before
          editor.insertText(index, '\n')
          index += 1

          editor.insertEmbed(index, 'codeBlock', {
            code,
            language,
          })

          // Always ensure newline after
          editor.insertText(index + 1, '\n')

          // Set cursor after the code block
          editor.setSelection(index + 2)
          editor.focus()
        }

        const newContent = editor.root.innerHTML
        setContent(newContent)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to insert code block:', error)
      }
    },
    [setContent]
  )

  const updateCodeBlock = React.useCallback(
    (code: string, language: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = (quillRef.current as any)?.getEditor?.()
      if (!editor || !editor.getModule('codeBlock')) {
        // eslint-disable-next-line no-console
        console.error('Editor or codeBlock module not available')
        return
      }

      const codeBlockModule = editor.getModule('codeBlock')
      let targetNode = editingCodeBlock?.node

      // Validate that the node exists and is still in the DOM
      if (!targetNode || !editor.container.contains(targetNode)) {
        // Try to find the node by its data attributes if it's stale
        // Use the original code/language from editingCodeBlock to find the node
        if (editingCodeBlock) {
          const codeBlocks = editor.container.querySelectorAll('.code-block')
          for (const block of codeBlocks) {
            const blockCode = block.getAttribute('data-code')
            const blockLanguage = block.getAttribute('data-language')
            // Match by original code and language before update
            if (blockCode === editingCodeBlock.code && blockLanguage === editingCodeBlock.language) {
              targetNode = block as HTMLElement
              break
            }
          }
        }

        // If still not found, fallback to inserting a new code block
        if (!targetNode || !editor.container.contains(targetNode)) {
          // eslint-disable-next-line no-console
          console.warn('Code block node not found, inserting new code block instead')
          insertCodeBlock(code, language)
          setEditingCodeBlock(null)
          return
        }
      }

      try {
        codeBlockModule.updateCodeBlock(targetNode, code, language, () => {
          const newContent = editor.root.innerHTML
          setContent(newContent)

          // Ensure Quill reflows without modifying content
          const currentSel = editor.getSelection(true)
          if (currentSel) {
            editor.setSelection(currentSel.index, currentSel.length, 'silent')
          } else {
            editor.update('silent')
          }
        })

        // Clear editing state after successful update
        setEditingCodeBlock(null)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update code block:', error)
        // Fallback: try to insert a new code block
        try {
          insertCodeBlock(code, language)
          setEditingCodeBlock(null)
        } catch (fallbackError) {
          // eslint-disable-next-line no-console
          console.error('Failed to insert code block as fallback:', fallbackError)
        }
      }
    },
    [editingCodeBlock, setContent, insertCodeBlock]
  )

  const handleBackdropClick = () => {
    setIsOpen(false)
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <Card
        className="relative z-[10001] border text-card-foreground flex w-full max-h-[calc(100vh-2rem)] max-w-lg flex-col overflow-hidden bg-white rounded-lg shadow-xl md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl"
        onClick={event => event.stopPropagation()}
      >
        <CardHeader className="flex shrink-0 flex-row items-center justify-between border-b p-4">
          <h2 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Chỉnh sửa bài viết' : 'Tạo bài viết'}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="min-h-0 flex-1 overflow-y-auto p-0">
          <div className="flex items-center gap-3 p-4">
            <Avatar className="h-10 w-10">
              <ResolvedAvatarImage avatar={user?.avatar} alt={`${user?.firstName} ${user?.lastName}`} />
              <AvatarFallback>
                {user?.firstName[0]}
                {user?.lastName[0]}
              </AvatarFallback>
            </Avatar>
            {/* TODO: Add user profile link */}
            <Link href={`/user/${user?.profileId || ''}`} passHref>
              <div className="flex flex-col hover:underline">
                <span className="font-semibold text-gray-900">
                  {user?.firstName} {user?.lastName}
                </span>
              </div>
            </Link>
          </div>

          {/* Anonymous Toggle Section */}
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isAnonymous ? <UserX className="h-4 w-4 text-gray-500" /> : <User className="h-4 w-4 text-gray-500" />}
                <Label htmlFor="anonymous-toggle" className="text-sm text-gray-700 cursor-pointer">
                  Đăng ẩn danh
                </Label>
              </div>
              <Switch id="anonymous-toggle" checked={isAnonymous} onCheckedChange={setIsAnonymous} disabled={loading} />
            </div>
            {isAnonymous && (
              <p className="text-xs text-gray-500 mt-1">Bài viết sẽ được đăng dưới tên "Người dùng ẩn danh"</p>
            )}
          </div>

          {/* Text Editor */}
          <div className="relative max-h-[500px] min-h-[360px]">
            <div className="max-h-[500px] min-h-[360px] overflow-y-auto">
              <QuillWrapper
                ref={quillRef as unknown as React.Ref<ReactQuill>}
                value={content}
                onChange={handleQuillChange}
                placeholder={`${user?.firstName || ''} ${user?.lastName || ''} ơi, bạn đang nghĩ gì thế?`}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    ['link'],
                    ['blockquote'],
                    [{ color: [] }, { background: [] }],
                    [{ font: [] }],
                    [{ align: [] }],
                  ],
                  hashtag: {},
                  math: {},
                  codeBlock: {},
                  mention: {},
                }}
                formats={[
                  'header',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'link',
                  'blockquote',
                  'color',
                  'background',
                  'font',
                  'align',
                  'emoji',
                  'hashtag',
                  'mention',
                  'customEmoji',
                  'math',
                  'codeBlock',
                ]}
                className="fsds-meme-editor relative"
                theme="snow"
              />
            </div>

            {/* Mention Dropdown — fixed so it is not clipped by the editor scroll container */}
            {showMentionDropdown && (isSearchingUsers || filteredUsers.length > 0) && (
              <div
                ref={dropdownRef}
                className="fixed z-[10002] w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                style={{
                  left: `${mentionPosition.x}px`,
                  top: `${mentionPosition.y}px`,
                }}
              >
                {isSearchingUsers && filteredUsers.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">Đang tìm người dùng...</div>
                ) : (
                  <ScrollArea className="h-full">
                    {filteredUsers.map((mentionUser, index) => (
                      <div
                        key={mentionUser.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                          index === selectedUserIndex ? 'bg-blue-50' : ''
                        }`}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => handleInsertMention(mentionUser)}
                      >
                        <Avatar className="h-6 w-6">
                          <ResolvedAvatarImage avatar={mentionUser.avatar} alt={mentionUser.displayName} />
                          <AvatarFallback className="text-xs">
                            {mentionUser.displayName.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-gray-700 truncate">{mentionUser.displayName}</span>
                      </div>
                    ))}
                  </ScrollArea>
                )}
              </div>
            )}
            {showMentionDropdown && mentionQuery.length === 0 && (
              <div
                className="fixed z-[10002] w-64 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500"
                style={{
                  left: `${mentionPosition.x}px`,
                  top: `${mentionPosition.y}px`,
                }}
              >
                Nhập tên để tìm người dùng...
              </div>
            )}
            {showMentionDropdown && mentionQuery.length > 0 && !isSearchingUsers && filteredUsers.length === 0 && (
              <div
                className="fixed z-[10002] w-64 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500"
                style={{
                  left: `${mentionPosition.x}px`,
                  top: `${mentionPosition.y}px`,
                }}
              >
                Không tìm thấy người dùng
              </div>
            )}
          </div>

          {/* Image Upload Section */}
          <div className={cn('px-4 py-2 flex flex-row items-center gap-2 justify-start')}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              aria-label="Upload images"
            />
            <Button
              variant="ghost"
              size="icon"
              className={cn('h-8 w-8 rounded-full hover:bg-gray-100', {})}
              onClick={imageHandler}
              disabled={isUploading}
            >
              <ImageIcon className="h-5 w-5 text-gray-500" />
            </Button>
            <EmojiPicker
              onEmojiSelect={insertEmoji}
              onCustomEmojiSelect={insertCustomEmoji}
              buttonIconClassName="!size-4"
              buttonClassName="!size-8 rounded-full"
            />
            {isUploading && <span className="text-sm text-gray-500 ml-2">Đang tải ảnh...</span>}
          </div>

          {/* Image Preview Section */}
          {uploadedImages.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {uploadedImages.map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    <Image
                      src={imageUrl}
                      alt="Uploaded"
                      className="w-16 h-16 object-cover rounded-lg"
                      width={64}
                      height={64}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity',
                        {
                          hidden: uploadedImages.length === 1 && isEditMode,
                        }
                      )}
                      onClick={() => removeImage(imageUrl)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </CardContent>

        <CardFooter className="shrink-0 border-t p-4">
          <Button
            className="w-full rounded-md bg-blue-500 py-2 font-medium text-white hover:bg-blue-600"
            disabled={!content.trim() || loading}
            onClick={handleSubmit}
          >
            {loading ? 'Đang xử lý...' : isEditMode ? 'Cập nhật bài viết' : 'Đăng bài'}
          </Button>
        </CardFooter>
      </Card>

      {/* Math Dialog */}
      <MathDialog
        key={isMathDialogOpen ? (editingMath ? 'math-edit' : 'math-new') : 'math-closed'}
        isOpen={isMathDialogOpen}
        onClose={() => {
          setIsMathDialogOpen(false)
          setEditingMath(null)
        }}
        onInsert={insertMath}
        onUpdate={updateMath}
        initialValue={editingMath?.latex || ''}
        isEditMode={!!editingMath}
      />

      {/* Code Block Dialog */}
      <CodeBlockDialog
        key={isCodeBlockDialogOpen ? (editingCodeBlock ? 'code-edit' : 'code-new') : 'code-closed'}
        isOpen={isCodeBlockDialogOpen}
        onClose={() => {
          setIsCodeBlockDialogOpen(false)
          setEditingCodeBlock(null)
        }}
        onInsert={insertCodeBlock}
        onUpdate={updateCodeBlock}
        initialCode={editingCodeBlock?.code || ''}
        initialLanguage={editingCodeBlock?.language || 'javascript'}
        isEditMode={!!editingCodeBlock}
      />

      <style jsx global>{`
        .fsds-meme-editor .ql-editor {
          border: none !important;
          padding: 12px 24px !important;
          font-size: 24px !important;
          line-height: 1.3 !important;
          color: #1c1e21 !important;
          min-height: 120px !important;
          /* Ensure proper text flow */
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        .fsds-meme-editor .ql-editor.ql-blank::before {
          color: #65676b !important;
          font-size: 24px !important;
          font-style: normal !important;
          left: 24px !important;
        }

        .fsds-meme-editor .ql-container {
          border: none !important;
          font-family: inherit !important;
        }

        .fsds-meme-editor .ql-editor:focus {
          outline: none !important;
        }

        .fsds-meme-editor .ql-toolbar.ql-snow {
          border: none !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background-color: #fff !important;
          border-bottom: 1px solid #e0e0e0 !important;
        }

        .highlight-hashtag {
          color: #1d4ed8;
          background-color: #e0f2fe;
          font-weight: 600;
          border-radius: 4px;
          padding: 0 2px;
        }

        /* Hashtag styles */
        .ql-hashtag {
          display: inline-block !important;
          line-height: 1 !important;
          padding: 2px 4px !important;
          border-radius: 4px !important;
          color: #1d4ed8 !important;
          background-color: #e0f2fe !important;
          text-decoration: none !important;
          cursor: pointer;
        }

        .ql-hashtag:hover {
          background-color: #bae6fd !important;
        }

        /* Mention styles — match chat (blue, clickable profile link) */
        .fsds-meme-editor .mention,
        .fsds-meme-editor .ql-mention {
          display: inline !important;
          font-weight: 700 !important;
          color: #1d4ed8 !important;
          background: transparent !important;
          text-decoration: none !important;
          cursor: pointer !important;
          margin: 0 !important;
        }

        .fsds-meme-editor .mention:hover,
        .fsds-meme-editor .ql-mention:hover {
          color: #1d4ed8 !important;
          text-decoration: underline !important;
        }

        /* Custom emoji styles */
        .custom-emoji-inline {
          display: inline-block !important;
          width: 24px !important;
          height: 24px !important;
          line-height: 1 !important;
          vertical-align: text-bottom !important; /* or 'middle' if you prefer */
          margin: 0 2px !important;
          border-radius: 4px !important;
          object-fit: cover !important;
          pointer-events: none; /* optional: so clicking doesn't move the caret oddly */
        }

        /* Math block styles */
        .math-block {
          display: block !important;
          background-color: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
          border-radius: 8px !important;
          padding: 12px 16px !important;
          margin: 8px 0 !important;
          cursor: pointer !important;
          user-select: none !important;
          text-align: center !important;
          transition: all 0.2s ease !important;
          position: relative !important;
          clear: both !important;
          width: 100% !important;
          box-sizing: border-box !important;
          pointer-events: auto !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          /* Ensure proper text flow */
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        .math-block:hover {
          background-color: #e9ecef !important;
          border-color: #dee2e6 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
        }

        .math-block::before {
          content: 'Click to edit' !important;
          position: absolute !important;
          top: -8px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          background-color: #6c757d !important;
          color: white !important;
          padding: 2px 8px !important;
          border-radius: 4px !important;
          font-size: 10px !important;
          opacity: 0 !important;
          transition: opacity 0.2s ease !important;
          pointer-events: none !important;
        }

        .math-block:hover::before {
          opacity: 1 !important;
        }

        /* KaTeX styles for math rendering */
        .math-block .katex {
          font-size: 1.2em !important;
          line-height: 1.4 !important;
        }

        .math-block .katex-display {
          margin: 0 !important;
          text-align: center !important;
        }

        /* Math error styles */
        .math-error {
          color: #dc3545 !important;
          font-style: italic !important;
          background-color: #f8d7da !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          border: 1px solid #f5c6cb !important;
        }

        /* Multiple formulas spacing */
        .math-block br {
          margin: 8px 0 !important;
        }

        /* Math preview styles */
        .math-preview {
          background-color: #f8f9fa !important;
          padding: 8px 12px !important;
          border-radius: 4px !important;
          border: 1px solid #e9ecef !important;
          text-align: center !important;
        }

        .math-preview .katex {
          font-size: 1.2em !important;
        }

        /* Math toolbar button */
        .ql-toolbar .ql-math {
          background: none !important;
          border: none !important;
          cursor: pointer !important;
          display: inline-block !important;
          height: 24px !important;
          padding: 2px 5px !important;
          width: 28px !important;
          font-size: 13px !important;
          font-weight: bold !important;
          color: #444 !important;
        }

        .ql-toolbar .ql-math:hover {
          color: #06c !important;
        }

        .ql-toolbar .ql-math.ql-active {
          color: #06c !important;
        }

        /* Code block toolbar button */
        .ql-toolbar .ql-codeblock {
          background: none !important;
          border: none !important;
          cursor: pointer !important;
          display: inline-block !important;
          height: 24px !important;
          padding: 2px 5px !important;
          width: 28px !important;
          font-size: 16px !important;
          font-weight: bold !important;
          color: #444 !important;
        }

        .ql-toolbar .ql-codeblock:hover {
          color: #06c !important;
        }

        .ql-toolbar .ql-codeblock.ql-active {
          color: #06c !important;
        }

        /* Code block styles */
        .code-block {
          display: block !important;
          background-color: transparent !important;
          border: none !important;
          margin: 8px 0 !important;
          cursor: pointer !important;
          user-select: none !important;
          position: relative !important;
          clear: both !important;
          width: 100% !important;
          box-sizing: border-box !important;
          pointer-events: auto !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        .code-block:hover {
          border-color: #555 !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
        }

        .code-block::before {
          content: 'Click to edit' !important;
          position: absolute !important;
          top: 0px !important;
          right: 8px !important;
          background-color: #6c757d !important;
          color: white !important;
          padding: 2px 8px !important;
          border-radius: 4px !important;
          font-size: 10px !important;
          opacity: 0 !important;
          transition: opacity 0.2s ease !important;
          pointer-events: none !important;
          z-index: 10 !important;
        }

        .code-block:hover::before {
          opacity: 1 !important;
        }

        .code-block pre {
          margin: 0 !important;
          padding: 8px !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
          flex: 1 !important;
        }

        /* Line numbers styling */
        .code-block > div[style*='display: flex'] {
          display: flex !important;
          margin: 0 !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          overflow: hidden !important;
        }

        .code-block > div[style*='display: flex'] > div[style*='background: #21252b'] {
          padding: 8px 8px 8px 12px !important;
          background: #21252b !important;
          color: #5c6370 !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          text-align: right !important;
          user-select: none !important;
          border-right: 1px solid #444 !important;
          min-width: 40px !important;
          flex-shrink: 0 !important;
        }

        /* Highlight.js atom-one-dark theme overrides for code blocks */
        .code-block .hljs {
          background: #282c34 !important;
          color: #abb2bf !important;
        }

        .code-block .hljs-keyword,
        .code-block .hljs-selector-tag,
        .code-block .hljs-built_in,
        .code-block .hljs-name {
          color: #c678dd !important;
        }

        .code-block .hljs-string,
        .code-block .hljs-attr {
          color: #98c379 !important;
        }

        .code-block .hljs-comment,
        .code-block .hljs-quote {
          color: #5c6370 !important;
          font-style: italic !important;
        }

        .code-block .hljs-number,
        .code-block .hljs-literal {
          color: #d19a66 !important;
        }

        .code-block .hljs-function,
        .code-block .hljs-title {
          color: #61afef !important;
        }

        .code-block .hljs-variable,
        .code-block .hljs-params {
          color: #e06c75 !important;
        }

        .code-block .hljs-type,
        .code-block .hljs-class {
          color: #e5c07b !important;
        }

        .code-block .hljs-operator {
          color: #56b6c2 !important;
        }

        .code-block .hljs-property {
          color: #61afef !important;
        }

        .code-block > div:first-child {
          background-color: #21252b !important;
          color: #abb2bf !important;
          padding: 8px 12px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          border-bottom: 1px solid #444 !important;
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
        }
      `}</style>
    </div>
  )
}
